import axios, { AxiosInstance } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

interface AuthResponse {
  token: string
  refreshToken: string
  user: {
    id: string
    username: string
    is_admin: boolean
    created_at: string
  }
}

interface RefreshResponse {
  token: string
  refreshToken: string
}

class APIClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.client.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config as any

        if (
          error?.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          originalRequest.url !== '/auth/refresh'
        ) {
          originalRequest._retry = true

          const refreshToken = this.getStoredRefreshToken()
          if (refreshToken) {
            try {
              const refreshResponse = await this.refreshSession(refreshToken)
              this.setToken(refreshResponse.token)
              this.setStoredRefreshToken(refreshResponse.refreshToken)
              localStorage.setItem('auth_token', refreshResponse.token)
              originalRequest.headers.Authorization = `Bearer ${refreshResponse.token}`
              return this.client(originalRequest)
            } catch {
              this.clearSession()
            }
          }
        }

        if (error?.response?.status === 401) {
          this.clearSession()
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }

        return Promise.reject(error)
      }
    )
  }

  private getStoredRefreshToken(): string | null {
    return localStorage.getItem('auth_refresh_token')
  }

  private setStoredRefreshToken(token: string | null) {
    if (token) {
      localStorage.setItem('auth_refresh_token', token)
    } else {
      localStorage.removeItem('auth_refresh_token')
    }
  }

  private clearSession() {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_refresh_token')
    localStorage.removeItem('auth_user')
    this.setToken(null)
  }

  setToken(token: string | null) {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete this.client.defaults.headers.common['Authorization']
    }
  }

  async refreshSession(refreshToken: string): Promise<RefreshResponse> {
    const response = await this.client.post('/auth/refresh', { refreshToken })
    return response.data
  }

  // Auth
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await this.client.post('/auth/login', { username, password })
    return response.data
  }

  async register(username: string, password: string): Promise<AuthResponse> {
    const response = await this.client.post('/auth/register', { username, password })
    return response.data
  }

  async logout(refreshToken: string | null) {
    await this.client.post('/auth/logout', { refreshToken })
    this.clearSession()
  }

  async getRegistrationStatus() {
    const response = await this.client.get('/auth/registration-status')
    return response.data
  }

  async checkAccessTokenStatus() {
    const response = await this.client.get('/auth/token-status')
    return response.data
  }

  async checkRefreshTokenStatus(refreshToken: string) {
    const response = await this.client.get('/auth/refresh-token-status', {
      params: { refreshToken }
    })
    return response.data
  }

  // Users
  async getCurrentUser() {
    const response = await this.client.get('/users/me')
    return response.data
  }

  // Groups
  async getGroups() {
    const response = await this.client.get('/groups')
    return response.data
  }

  async createGroup(name: string) {
    const response = await this.client.post('/groups', { name })
    return response.data
  }

  async getGroup(id: string) {
    const response = await this.client.get(`/groups/${id}`)
    return response.data
  }

  async renameGroup(id: string, name: string) {
    const response = await this.client.put(`/groups/${id}`, { name })
    return response.data
  }

  async leaveGroup(id: string) {
    const response = await this.client.delete(`/groups/${id}`)
    return response.data
  }

  async getGroupMembers(groupId: string) {
    const response = await this.client.get(`/groups/${groupId}/members`)
    return response.data
  }

  async inviteUser(groupId: string, username: string) {
    const response = await this.client.post(`/groups/${groupId}/invites`, { username })
    return response.data
  }

  async acceptInvite(inviteId: string) {
    const response = await this.client.post(`/groups/invites/${inviteId}/accept`)
    return response.data
  }

  async declineInvite(inviteId: string) {
    const response = await this.client.post(`/groups/invites/${inviteId}/decline`)
    return response.data
  }

  async getPendingInvites() {
    const response = await this.client.get('/groups/invites/pending')
    return response.data
  }

  async setFavoriteGroup(groupId: string) {
    const response = await this.client.post(`/groups/${groupId}/favorite`)
    return response.data
  }

  // Eating Records
  async recordEating(eatingTime: string) {
    const response = await this.client.post('/eating-records', {
      eating_time: eatingTime,
    })
    return response.data
  }

  async getLatestEatingRecord() {
    const response = await this.client.get('/eating-records/latest')
    return response.data
  }

  async deleteLatestEatingRecord() {
    const response = await this.client.delete('/eating-records/latest')
    return response.data
  }

  async getGroupLatestEatingRecords(groupId: string) {
    const response = await this.client.get(`/groups/${groupId}/eating-records/latest`)
    return response.data
  }

  async getFavoriteGroupLatestEatingRecords() {
    const response = await this.client.get('/groups/favorite/eating-records/latest')
    return response.data
  }

  async getUserEatingHistory(limit = 30) {
    const response = await this.client.get(`/eating-records/history?limit=${limit}`)
    return response.data
  }

  // Auth
  async changePassword(currentPassword: string, newPassword: string): Promise<{ token: string; refreshToken: string }> {
    const response = await this.client.put('/auth/change-password', { currentPassword, newPassword })
    return response.data
  }

  // Settings
  async getSettings() {
    const response = await this.client.get('/settings')
    return response.data
  }

  async updateSettings(settings: { date_format?: string; time_format?: string; fasting_interval?: number }) {
    const response = await this.client.put('/settings', settings)
    return response.data
  }

  // Admin
  async getUsers() {
    const response = await this.client.get('/admin/users')
    return response.data
  }

  async createUser(username: string, password: string) {
    const response = await this.client.post('/admin/users', { username, password })
    return response.data
  }

  async changeUserRole(userId: string, isAdmin: boolean) {
    const response = await this.client.put(`/admin/users/${userId}/role`, { is_admin: isAdmin })
    return response.data
  }

  async setUserPassword(userId: string, password: string) {
    const response = await this.client.put(`/admin/users/${userId}/password`, { password })
    return response.data
  }

  async deleteUser(userId: string) {
    const response = await this.client.delete(`/admin/users/${userId}`)
    return response.data
  }

  // Health
  async getHealth() {
    const response = await this.client.get('/health')
    return response.data
  }
}

export const API = new APIClient()